import { Controller } from "stimulus";

// Drives the quotation PDF modal on the shipment finance page: one text area
// with a save-only action and a save-and-download action. Validation errors
// come back as JSON and are shown in the modal, leaving the typed text alone.
//
// Spec: docs/specs/2026-09-15-shp-18427-quotation-pdf-comment.md
export default class extends Controller {
  static targets = ["error", "notice"];
  static values = { modal: String };

  downloadFile(event) {
    this.startRequest(event);

    this.request(event.currentTarget.getAttribute("formaction") || this.element.action)
      .then((response) => this.handleDownload(response))
      .finally(() => this.enableButton());
  }

  saveComment(event) {
    this.startRequest(event);

    this.request(event.currentTarget.getAttribute("formaction") || this.element.action)
      .then((response) => this.handleSave(response))
      .finally(() => this.enableButton());
  }

  startRequest(event) {
    event.preventDefault();
    this.clearMessages();
    this.disableButton(event.currentTarget);
  }

  request(url) {
    return fetch(url, {
      method: this.element.method.toUpperCase(),
      headers: this.headers(),
      body: new FormData(this.element),
    });
  }

  // The form carries a per-form CSRF token bound to its own action, so the Save
  // comment button - which posts elsewhere via formaction - has to send the
  // global token from the meta tag instead.
  headers() {
    const headers = { Accept: "application/json, application/pdf" };
    const token = document.querySelector('meta[name="csrf-token"]');
    if (token) headers["X-CSRF-Token"] = token.getAttribute("content");

    return headers;
  }

  handleDownload(response) {
    if (!response.ok) return this.showErrors(response);

    // Example of Content-Disposition header:
    // "attachment; filename="FILENAME_FROM_SERVER_WITH_EXTENSION".
    // We're extracting the filename using a named capturing group
    const rgex = /filename="(?<filename>.+)"/g;
    const filename = rgex.exec(response.headers.get("Content-Disposition")).groups.filename;

    return response.blob().then((blob) => {
      this.downloadBlob(blob, filename);
      this.closeModal();
    });
  }

  handleSave(response) {
    if (!response.ok) return this.showErrors(response);

    return this.showNotice("Comment saved.");
  }

  showErrors(response) {
    return response
      .json()
      .then((data) => (data.errors || []).join(", "))
      .catch(() => "")
      .then((message) =>
        this.showError(message || "The comment could not be saved. Please try again.")
      );
  }

  showError(message) {
    this.toggleMessage(this.errorTarget, message);
  }

  showNotice(message) {
    this.toggleMessage(this.noticeTarget, message);
  }

  toggleMessage(element, message) {
    element.textContent = message;
    element.classList.remove("d-none");
  }

  clearMessages() {
    [this.errorTarget, this.noticeTarget].forEach((element) => {
      element.textContent = "";
      element.classList.add("d-none");
    });
  }

  closeModal() {
    if (this.hasModalValue) $(this.modalValue).modal("hide");
  }

  disableButton(button) {
    this.buttonElement = button;
    this.enableWith = button.value;
    button.disabled = true;
    button.value = "Working...";
  }

  enableButton() {
    if (!this.buttonElement) return;

    this.buttonElement.disabled = false;
    this.buttonElement.value = this.enableWith;
  }

  downloadBlob(blob, name = "") {
    // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
    const blobUrl = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement("a");

    // Set link's href to point to the Blob URL
    link.href = blobUrl;
    link.download = name;

    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );

    // Remove link from body
    document.body.removeChild(link);
  }
}
